import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { productService } from '../services/productService';
import type {
  Comment,
  CreateCommentPayload,
  UpdateCommentPayload,
  CommentListResponse,
} from '../services/productService';
import { useState, useEffect } from 'react';

/**
 * React Query hooks for managing product comments
 *
 * Provides comprehensive comment management with:
 * - Optimistic updates
 * - Error handling with user feedback
 * - Automatic cache invalidation
 * - Loading states
 */

interface UseCommentsOptions {
  flat?: boolean;
  parent_id?: number | 'null';
  search?: string;
  page?: number;
  page_size?: number;
  ordering?: string; // Add ordering support (e.g., 'created_at', '-created_at')
}

/**
 * Hook for fetching comments for a product
 */
export function useComments(productId: number, options: UseCommentsOptions = {}) {
  return useQuery({
    queryKey: ['comments', productId, options],
    queryFn: () => productService.getComments(productId, options),
    enabled: !!productId,
    staleTime: 30000, // 30 seconds
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook for fetching a specific comment
 */
export function useComment(productId: number, commentId: number) {
  return useQuery({
    queryKey: ['comment', productId, commentId],
    queryFn: () => productService.getComment(productId, commentId),
    enabled: !!productId && !!commentId,
    staleTime: 60000, // 1 minute
    retry: 2,
  });
}

/**
 * Hook for creating a new comment
 */
export function useCreateComment(productId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateCommentPayload) => productService.createComment(productId, payload),

    onMutate: async newComment => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['comments', productId] });

      // Snapshot the previous value
      const previousComments = queryClient.getQueryData<CommentListResponse>([
        'comments',
        productId,
      ]);

      // Optimistically update to the new value
      if (previousComments) {
        const optimisticComment: Comment = {
          id: Date.now(), // Temporary ID
          body: newComment.body,
          author: 0, // Will be filled by server
          author_name: 'You',
          author_full_name: 'You',
          parent: newComment.parent || null,
          is_reply: !!newComment.parent,
          thread_depth: 0,
          replies_count: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          replies: [],
        };

        queryClient.setQueryData<CommentListResponse>(['comments', productId], {
          ...previousComments,
          results: [optimisticComment, ...previousComments.results],
          count: previousComments.count + 1,
          total_comments: previousComments.total_comments + 1,
          top_level_comments: newComment.parent
            ? previousComments.top_level_comments
            : previousComments.top_level_comments + 1,
        });
      }

      return { previousComments };
    },

    onError: (error, newComment, context) => {
      // Rollback on error
      if (context?.previousComments) {
        queryClient.setQueryData(['comments', productId], context.previousComments);
      }

      toast.error('Failed to create comment', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    },

    onSuccess: data => {
      toast.success('Comment created successfully');
    },

    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['comments', productId] });
    },
  });
}

/**
 * Hook for updating an existing comment
 */
export function useUpdateComment(productId: number, commentId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateCommentPayload) =>
      productService.updateComment(productId, commentId, payload),

    onMutate: async updatedComment => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['comments', productId] });
      await queryClient.cancelQueries({
        queryKey: ['comment', productId, commentId],
      });

      // Snapshot the previous values
      const previousComments = queryClient.getQueryData<CommentListResponse>([
        'comments',
        productId,
      ]);
      const previousComment = queryClient.getQueryData<Comment>(['comment', productId, commentId]);

      // Optimistically update comments list
      if (previousComments) {
        const updatedResults = previousComments.results.map(comment => {
          if (comment.id === commentId) {
            return {
              ...comment,
              body: updatedComment.body,
              updated_at: new Date().toISOString(),
            };
          }
          // Also check replies
          if (comment.replies) {
            return {
              ...comment,
              replies: comment.replies.map(reply =>
                reply.id === commentId
                  ? {
                      ...reply,
                      body: updatedComment.body,
                      updated_at: new Date().toISOString(),
                    }
                  : reply
              ),
            };
          }
          return comment;
        });

        queryClient.setQueryData<CommentListResponse>(['comments', productId], {
          ...previousComments,
          results: updatedResults,
        });
      }

      // Optimistically update individual comment
      if (previousComment) {
        queryClient.setQueryData<Comment>(['comment', productId, commentId], {
          ...previousComment,
          body: updatedComment.body,
          updated_at: new Date().toISOString(),
        });
      }

      return { previousComments, previousComment };
    },

    onError: (error, updatedComment, context) => {
      // Rollback on error
      if (context?.previousComments) {
        queryClient.setQueryData(['comments', productId], context.previousComments);
      }
      if (context?.previousComment) {
        queryClient.setQueryData(['comment', productId, commentId], context.previousComment);
      }

      toast.error('Failed to update comment', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    },

    onSuccess: () => {
      toast.success('Comment updated successfully');
    },

    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['comments', productId] });
      queryClient.invalidateQueries({
        queryKey: ['comment', productId, commentId],
      });
    },
  });
}

/**
 * Hook for deleting a comment
 */
export function useDeleteComment(productId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: number) => productService.deleteComment(productId, commentId),

    onMutate: async commentId => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['comments', productId] });

      // Snapshot the previous value
      const previousComments = queryClient.getQueryData<CommentListResponse>([
        'comments',
        productId,
      ]);

      // Optimistically remove the comment
      if (previousComments) {
        const filteredResults = previousComments.results.filter(comment => {
          // Remove the comment itself
          if (comment.id === commentId) return false;

          // Also filter out from replies
          if (comment.replies) {
            comment.replies = comment.replies.filter(reply => reply.id !== commentId);
          }

          return true;
        });

        queryClient.setQueryData<CommentListResponse>(['comments', productId], {
          ...previousComments,
          results: filteredResults,
          count: Math.max(0, previousComments.count - 1),
          total_comments: Math.max(0, previousComments.total_comments - 1),
        });
      }

      return { previousComments };
    },

    onError: (error, commentId, context) => {
      // Rollback on error
      if (context?.previousComments) {
        queryClient.setQueryData(['comments', productId], context.previousComments);
      }

      toast.error('Failed to delete comment', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    },

    onSuccess: () => {
      toast.success('Comment deleted successfully');
    },

    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['comments', productId] });
    },
  });
}

/**
 * Hook for managing comment search
 */
export function useCommentSearch(productId: number, initialSearch = '', ordering?: string) {
  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const commentsQuery = useComments(productId, {
    search: debouncedSearch || undefined,
    flat: true, // Use flat view for search results
    ordering: ordering,
  });

  return {
    search,
    setSearch,
    debouncedSearch,
    ...commentsQuery,
  };
}
